import { useQuery } from 'urql';
import { isUndefined } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { PostInsights, GetSocialAnalyticsPostDetailsQuery } from '@frontend/gql/social/graphql';
import { logger } from '@common';
import { getSocialAnalyticsPostDetails } from '../graphql';
import { QueryVariables } from '../types';
import { Project, Group } from '../components/filters-form/types';
import { EnrichedPost, LocalUpdate } from './usePostsData';

export type EnrichedPostDetailsData = Omit<GetSocialAnalyticsPostDetailsQuery, 'client'> & {
  client?: Omit<NonNullable<GetSocialAnalyticsPostDetailsQuery['client']>, 'socialAnalytics'> & {
    socialAnalytics?: Omit<
      NonNullable<NonNullable<GetSocialAnalyticsPostDetailsQuery['client']>['socialAnalytics']>,
      'posts'
    > & {
      posts?: Omit<
        NonNullable<NonNullable<NonNullable<GetSocialAnalyticsPostDetailsQuery['client']>['socialAnalytics']>['posts']>,
        'results'
      > & {
        results: EnrichedPost[];
      };
    };
  };
};

export const usePostDetails = ({
  clientId,
  postId,
  queryVariables,
  pause,
  projects,
  groups,
}: {
  clientId: string;
  postId?: string;
  queryVariables: QueryVariables;
  pause?: boolean;
  projects: Project[];
  groups: Group[];
}) => {
  const [localUpdates, setLocalUpdates] = useState<Record<string, LocalUpdate>>({});

  const [{ data: postDetailData, fetching, error }] = useQuery<GetSocialAnalyticsPostDetailsQuery>({
    query: getSocialAnalyticsPostDetails,
    variables: {
      clientId,
      startDate: queryVariables.startDate || '',
      endDate: queryVariables.endDate || '',
      ...(postId ? { postEntityIds: [postId] } : {}),
      includeAds: queryVariables.includeAds,
      includeEstimates: queryVariables.includeEstimates,
      postDateIntervalEnabled: queryVariables.postDateIntervalEnabled || false,
      lifetimeInsightsEnabled: queryVariables.lifetimeInsightsEnabled || false,
    },
    pause: pause || !clientId || !postId || isUndefined(queryVariables.includeAds),
  });

  const updateLocalAssignment = useCallback((update: Omit<LocalUpdate, 'timestamp'>) => {
    setLocalUpdates((prev) => {
      logger.info('Updating local assignment (post detail):', {
        entityId: update.entityId,
        previousProject: prev[update.entityId]?.projectId,
        newProjectId: update.projectId,
        newProjectName: update.projectName,
        previousGroup: prev[update.entityId]?.groupId,
        newGroupId: update.groupId,
        newGroupName: update.groupName,
      });

      const newUpdate = {
        ...update,
        timestamp: Date.now(),
      };

      logger.debug('Local updates state (post detail):', {
        totalUpdates: Object.keys(prev).length,
        updatedEntityId: update.entityId,
        timestamp: new Date(newUpdate.timestamp).toISOString(),
      });

      return {
        ...prev,
        [update.entityId]: newUpdate,
      };
    });
  }, []);

  const resolveProject = useCallback(
    (
      post: PostInsights,
      localUpdate: LocalUpdate | undefined,
      useLocalUpdate: boolean,
    ): { id: number; name: string } | null => {
      let project: Project | undefined;
      if (useLocalUpdate && localUpdate?.projectId) {
        project = projects.find((p) => p.id.toString() === localUpdate.projectId);
        logger.debug('Project resolution (local update - post detail):', {
          postId: post.entityId,
          localUpdateProjectId: localUpdate.projectId,
          foundProject: project?.name,
          fallbackName: localUpdate.projectName,
        });
        if (!project && localUpdate.projectName) {
          const numericId = Number(localUpdate.projectId);
          project = {
            id: Number.isFinite(numericId) ? numericId : -1,
            name: localUpdate.projectName,
          };
        }
      } else if (post.projectId) {
        project = projects.find((p) => p.id.toString() === post.projectId);
        if (!project) {
          const numericId = Number(post.projectId);
          project = {
            id: Number.isFinite(numericId) ? numericId : -1,
            name: `Unknown(${post.projectId})`,
          };
        }
      }
      return project ? { id: project.id, name: project.name } : null;
    },
    [projects],
  );

  const resolveGroup = useCallback(
    (
      post: PostInsights,
      localUpdate: LocalUpdate | undefined,
      useLocalUpdate: boolean,
    ): { id: number; name: string } | null => {
      let group: Group | undefined;
      if (useLocalUpdate && localUpdate?.groupId) {
        group = groups.find((g) => g.id.toString() === localUpdate.groupId);
        logger.debug('Group resolution (local update - post detail):', {
          postId: post.entityId,
          localUpdateGroupId: localUpdate.groupId,
          foundGroup: group?.name,
          fallbackName: localUpdate.groupName,
        });
        if (!group && localUpdate.groupName) {
          const numericId = Number(localUpdate.groupId);
          group = {
            id: Number.isFinite(numericId) ? numericId : -1,
            name: localUpdate.groupName,
          };
        }
      } else if (post.groupId) {
        group = groups.find((g) => g.id.toString() === post.groupId);
        if (!group) {
          const numericId = Number(post.groupId);
          group = {
            id: Number.isFinite(numericId) ? numericId : -1,
            name: `Unknown(${post.groupId})`,
          };
        }
      }
      return group ? { id: group.id, name: group.name } : null;
    },
    [groups],
  );

  const enrichedData = useMemo<EnrichedPostDetailsData | undefined>(() => {
    if (!postDetailData?.client?.socialAnalytics?.posts?.results) {
      return postDetailData as EnrichedPostDetailsData;
    }

    const enrichedPosts: EnrichedPost[] = postDetailData.client.socialAnalytics.posts.results
      .map((post) => {
        if (!post) return null;
        const localUpdate = localUpdates[post.entityId];
        const useLocalUpdate = localUpdate && Date.now() - localUpdate.timestamp < 5 * 60 * 1000;

        if (useLocalUpdate) {
          logger.debug('Applying local update (post detail):', {
            postId: post.entityId,
            mentionId: post.mentionId,
            originalProjectId: post.projectId,
            originalGroupId: post.groupId,
            newProjectId: localUpdate.projectId,
            newGroupId: localUpdate.groupId,
            updateAge: `${Math.round((Date.now() - localUpdate.timestamp) / 1000)}s`,
          });
        }

        const enrichedPost: EnrichedPost = {
          ...post,
          projectId: useLocalUpdate ? localUpdate?.projectId : post.projectId,
          groupId: useLocalUpdate ? localUpdate?.groupId : post.groupId,
          project: resolveProject(post, localUpdate, useLocalUpdate),
          group: resolveGroup(post, localUpdate, useLocalUpdate),
        };
        return enrichedPost;
      })
      .filter((post): post is EnrichedPost => post !== null);

    logger.info('Completed post detail enrichment:', {
      totalProcessed: enrichedPosts.length,
      postsWithProjects: enrichedPosts.filter((p) => p.project).length,
      postsWithGroups: enrichedPosts.filter((p) => p.group).length,
    });

    return {
      ...postDetailData,
      client: {
        ...postDetailData.client,
        socialAnalytics: {
          ...postDetailData.client.socialAnalytics,
          posts: {
            ...postDetailData.client.socialAnalytics.posts,
            results: enrichedPosts,
          },
        },
      },
    };
  }, [postDetailData, resolveProject, resolveGroup, localUpdates]);

  return {
    data: enrichedData,
    error,
    fetching,
    updateLocalAssignment,
  };
};
